/*
    Copyright 2020 Lauren Gyldenlove.
    This file is part of Duelmaster.

    Duelmaster is free software: you can redistribute it and/or modify
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Duelmaster is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with Foobar.  If not, see <https://www.gnu.org/licenses/>.
 */

import React, { useState, ChangeEvent } from 'react';
import { createStyles, makeStyles, useTheme, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import * as Ledger from '../model/Ledger';
import { AppContext } from '../App';
import { AppSettings } from '../model/AppSettings';
import { IconButton, Grid, SvgIcon, Dialog, DialogContent, Tooltip, Typography, useMediaQuery } from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import { mdiSwordCross } from '@mdi/js';
import { BattleCalculatorField } from './BattleCalculatorField';
import { Monster } from './Monster';
import * as Calculator from '../model/Calculator';
import * as ShortcutManager from '../model/ShortcutManager';
import CloseIcon from '@material-ui/icons/Close';

const styles = (theme: Theme) => 
    createStyles({
        battleHeader: {
            paddingTop: "1em",
            textAlign: "center"
        },
        battleCalculatorColumn: {
            //background: 'rgba(10, 10, 10, 0.8)',
            //borderRadius: '5px',
            padding: "1em"
        },
        padSides: {
            padding: "0.5em"
        },
        battleButtonGridItem: {
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center"
        },
        battleButton: {
            backgroundColor: theme.palette.secondary.dark,
            color: theme.palette.secondary.contrastText,
            "&:hover": {
                background: theme.palette.secondary.main,
            }
        },
        battleIcon: {
            fontSize: 40
        },
        openBattleCalculatorWrapper: {
            width: "100%",
            display: "inline-block",
        },
        openBattleCalculatorButton: {
            paddingBottom: "10%",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            textTransform: "uppercase",
            fontWeight: "bold",
            borderColor: theme.palette.primary.main,
            borderWidth: "4px",
            //padding: "0.25em",
            "&:hover": {
                background: theme.palette.primary.light
            }
        },
        root: {
            margin: 0,
            padding: theme.spacing(2)
        },
        closeButton: {
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500]
        }
    });

const useStyles = makeStyles(styles);

export interface BattleCalculatorProps {
    leftLedger: Ledger.Model
    setLeftLedger: Function
    rightLedger: Ledger.Model
    setRightLedger: Function
}

export interface CustomDialogTitleProps extends WithStyles<typeof styles> {
    id: string;
    children: React.ReactNode;
    onClose: () => void;
}

export const BattleCalculator = (props: BattleCalculatorProps) => {
	const [open, setOpen] = React.useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [leftPlayerStrength, setLeftPlayerStrength] = useState(0);
    const [rightPlayerStrength, setRightPlayerStrength] = useState(0);

    const updateLeftPlayerStrength = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let strength = Calculator.parseCalculation(event.currentTarget.value);
        setLeftPlayerStrength(strength);
    }
    
    const updateRightPlayerStrength = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let strength = Calculator.parseCalculation(event.currentTarget.value);
        setRightPlayerStrength(strength);
    }

    const handleOpen = () => {
        setOpen(true);
    }

    const handleClose = () => {
        setOpen(false);
        setLeftPlayerStrength(0);
        setRightPlayerStrength(0);
    }

    ShortcutManager.register('b', () => {
        setOpen(!open);
    });

    function calculateDamage (leftPlayerStrength: number, rightPlayerStrength: number): void {
        if(leftPlayerStrength > rightPlayerStrength) {
            props.setRightLedger(
                Ledger.addBattleDamage(props.rightLedger, leftPlayerStrength - rightPlayerStrength)
            );
            handleClose();
        } else if(rightPlayerStrength > leftPlayerStrength) {
            props.setLeftLedger(
                Ledger.addBattleDamage(props.leftLedger, rightPlayerStrength - leftPlayerStrength)
            );
            handleClose();
        }
    };


    const classes = useStyles();
    const CustomDialogTitle = withStyles(styles)((props: CustomDialogTitleProps) => {
        const { children, classes, onClose, ...other } = props;
        return (
            <MuiDialogTitle disableTypography className={classes.root} {...other}>
                <Typography variant="h6">{children}</Typography>
                {onClose ? (
                    <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                ) : null}
            </MuiDialogTitle>
        
        )
    });
    
    return (
        <AppContext.Consumer>
            {(settings: AppSettings) => {                
                return (
                    <React.Fragment>
                        <div className={classes.openBattleCalculatorWrapper}>
                            <IconButton className={classes.openBattleCalculatorButton} onClick={handleOpen}>
                                <SvgIcon className={classes.battleIcon}>
                                    <path d={mdiSwordCross} />
                                </SvgIcon>
                            </IconButton>
                        </div>
                        <Dialog fullScreen={fullScreen} fullWidth open={open} onClose={handleClose} aria-labelledby="battle-calculator-dialog-title">
                            <CustomDialogTitle id="battle-calculator-dialog-title" onClose={handleClose}>Battle Calculator</CustomDialogTitle>
                            <DialogContent>
                                <form onSubmit={(event) => { event.preventDefault(); calculateDamage(leftPlayerStrength, rightPlayerStrength); }}>
                                    <Grid container justify="center" align-items="center">
                                        <Grid item md={12}></Grid>
                                        <Grid item className={classes.battleCalculatorColumn} xs={12} sm={4}>
                                            <Typography className="t-center" variant="h6">{settings.leftPlayer.name}'s Monster</Typography>
                                            <Monster monsterStrength={Math.floor(leftPlayerStrength)} />
                                            <BattleCalculatorField fieldId={"leftPlayerStrength"} playerName={settings.leftPlayer.name} updatePlayerStrength={updateLeftPlayerStrength} />
                                        </Grid>
                                        <Grid item xs={12} sm={1} className={classes.battleButtonGridItem}>
                                            <Tooltip title="Calculate battle damage">
                                                <IconButton className={classes.battleButton} onClick={(event) => calculateDamage(leftPlayerStrength, rightPlayerStrength)}>
                                                    <SvgIcon className={classes.battleIcon}>
                                                        <path d={mdiSwordCross} />
                                                    </SvgIcon>
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                        <Grid item className={classes.battleCalculatorColumn} xs={12} sm={4}>
                                            <Typography className="t-center" variant="h6">{settings.rightPlayer.name}'s Monster</Typography>
                                            <Monster monsterStrength={Math.floor(rightPlayerStrength)} />
                                            <BattleCalculatorField fieldId={"rightPlayerStrength"} playerName={settings.rightPlayer.name} updatePlayerStrength={updateRightPlayerStrength} />                      
                                        </Grid>
                                    </Grid>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </React.Fragment>
                );
            }
        }
        </AppContext.Consumer>
    )
};
